import { createBlocksClient } from "@seliseblocks/client";
import { BLOCKS_API_URL, PROJECT_KEY } from "../../blocks/config";
import apiClient from "../axios";

/**
 * Clients for the engagement Functions:
 * - `club-stickers-v1` (place/remove a sticker; the only writer of post.stickers)
 * - `club-post-counters-v1` (recount a post's reaction/comment/save counters and score)
 *
 * Unset EXPO_PUBLIC_STICKERS_FUNCTION_ID hides sticker mode; unset
 * EXPO_PUBLIC_COUNTERS_FUNCTION_ID keeps the legacy client-side counter writes.
 */
const stickersId = process.env.EXPO_PUBLIC_STICKERS_FUNCTION_ID ?? "";
const countersId = process.env.EXPO_PUBLIC_COUNTERS_FUNCTION_ID ?? "";
export const stickersEnabled = !!stickersId;
export const countersEnabled = !!countersId;

export type StickerPlacement = {
  userId: string;
  stickerId: string;
  mediaIndex: number;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  displayName?: string;
  profileImageUrl?: string;
  placedAt?: string;
};

export type PlaceStickerInput = Omit<StickerPlacement, "userId" | "placedAt"> & { postId: string };
export type PlaceStickerResult = { created: boolean; stickerCount: number; sticker: StickerPlacement; postAuthorId?: string };

export class EngagementFunctionError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
  }
}

const makeClient = () =>
  createBlocksClient({
    apiUrl: BLOCKS_API_URL,
    xBlocksKey: PROJECT_KEY,
    fetch: async (url, init) => {
      const response = await apiClient.request({
        url: String(url), method: init?.method, data: init?.body,
        headers: Object.fromEntries(new Headers(init?.headers).entries()),
        timeout: 30000, validateStatus: (status) => status !== 401,
      });
      return new Response(JSON.stringify(response.data), {
        status: response.status, headers: { "content-type": "application/json" },
      });
    },
  });

/**
 * The platform either passes the handler's status through as the HTTP status
 * (body = handler body) or wraps it as `{statusCode, body}`. Accept both.
 */
export const unwrapFunctionResult = (value: any) => {
  if (value && typeof value.statusCode === "number") {
    const body = typeof value.body === "string" ? safeJson(value.body) : value.body;
    if (value.statusCode >= 400) throw new EngagementFunctionError(body?.code ?? "ENGAGEMENT_UNAVAILABLE", value.statusCode);
    return body;
  }
  if (value && typeof value.http === "number" && value.http >= 400) {
    throw new EngagementFunctionError(value.code ?? "ENGAGEMENT_UNAVAILABLE", value.http);
  }
  return value;
};
const safeJson = (s: string) => { try { return JSON.parse(s); } catch { return {}; } };

async function invoke<T>(id: string, body: Record<string, unknown>): Promise<T> {
  if (!id) throw new EngagementFunctionError("ENGAGEMENT_NOT_CONFIGURED", 503);
  const blocks = makeClient();
  let value: any;
  try {
    value = await blocks.http.request(`/logic/v4/fn/${encodeURIComponent(id)}`, {
      method: "POST", body, headers: { Prefer: "wait=25" },
    });
  } catch (error: any) {
    const b = typeof error?.body === "string" ? safeJson(error.body) : error?.body;
    throw new EngagementFunctionError(b?.code ?? b?.body?.code ?? "ENGAGEMENT_UNAVAILABLE", error?.status ?? 503);
  }
  if (value?.runId && value?.pollToken) {
    const deadline = Date.now() + 30000;
    for (let delay = 500; Date.now() < deadline; delay = Math.min(delay * 1.5, 4000)) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      const result: any = await blocks.http.request(`/logic/v4/fn/runs/${encodeURIComponent(value.runId)}/result`, {
        headers: { "x-poll-token": value.pollToken },
      });
      const status = String(result.status).toUpperCase();
      if (status === "SUCCEEDED") return unwrapFunctionResult(typeof result.result === "string" ? JSON.parse(result.result) : result.result) as T;
      if (["FAILED", "TIMEDOUT", "TIMED_OUT", "CANCELLED", "CANCELED"].includes(status)) throw new EngagementFunctionError("ENGAGEMENT_FAILED", 503);
    }
    throw new EngagementFunctionError("ENGAGEMENT_TIMEOUT", 504);
  }
  return unwrapFunctionResult(value) as T;
}

export const placeSticker = (input: PlaceStickerInput) =>
  invoke<PlaceStickerResult>(stickersId, { action: "place", ...input });

export const removeSticker = (postId: string, userId?: string) =>
  invoke<{ removed: boolean; stickerCount: number }>(stickersId, { action: "remove", postId, ...(userId ? { userId } : {}) });

/** Fire-and-forget server recount; failures self-heal on the next engagement. */
export async function recountPost(postId: string | undefined) {
  if (!countersEnabled || !postId) return null;
  try {
    return await invoke<any>(countersId, { action: "recount", postId });
  } catch (error) {
    console.warn("recountPost failed", (error as Error)?.message);
    return null;
  }
}
